import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import EBPropTypes from 'js-utils/prop-types';
import gettext from 'js-utils/gettext';

import EmptyState from 'eventbrite_design_system/emptyState/EmptyState';
import Button from 'eventbrite_design_system/button/Button';

import { collections as collectionsDuck } from '@eb/destination-utils';
import { GA_SETTINGS_PROP_TYPE } from '@eb/site-analytics';

import EventCardGroup from './EventCardGroup';
import ConditionalComponent from './ConditionalComponent';

import { EVENT_SHAPE_PROPTYPE, BROWSE_EVENTS_PATH } from '../constants';

import './eventsSection.scss';

const {
    constants: { COLLECTION_PROP_TYPE },
} = collectionsDuck;
/*
 * Mobile and page specific display related formatting
 * for the event cards.
 */
const _formatEventsForDisplay = (events, isMobile) =>
    events.map((event) => ({
        ...event,
        type: 'list',
        containerSpacingClasses: isMobile ? '' : 'eds-l-pad-all-3',
        excludeDateThumbnail: true,
    }));

const SeeMoreButton = ({ hasPagination, onClick, isLoading }) => (
    <ConditionalComponent
        shouldRender={hasPagination}
        containerClassName="eds-align--center eds-l-pad-bot-4"
    >
        <Button style="link" onClick={onClick} isLoading={isLoading}>
            {gettext('Show More')}
        </Button>
    </ConditionalComponent>
);

export default class EventsSection extends PureComponent {
    static propTypes = {
        /*
         * Callback for if a user saves an event
         */
        onSaveEvent: PropTypes.func,
        /*
         * Array of Events from organizers that the user
         * currently follows.
         */
        events: PropTypes.arrayOf(EVENT_SHAPE_PROPTYPE),
        /*
         * is the page is in a loading state
         */
        isLoading: PropTypes.bool,
        /*
         * If a call was made to get events which returned none
         */
        userHasNoEvents: PropTypes.bool,
        /*
         * Callback when a user views an event
         */
        handleOnView: PropTypes.func,
        /*
         * Callback when a user attempts to share an event
         */
        handleOnWillShare: PropTypes.func,
        /*
         * Callback when a user shares an event
         */
        handleOnDidShare: PropTypes.func,
        /*
         * Empty state configuration
         */
        emptyStateConfig: PropTypes.shape({
            graphicType: PropTypes.node,
            description: EBPropTypes.translation,
            title: EBPropTypes.translation,
        }),
        /*
         * `true` if feature flag is enabled
         */
        showFeaturedOrganizers: PropTypes.bool,
        /*
         * Category for Google Analytics, used by follow organizer
         */
        gaCategory: PropTypes.string,
        /*
         * Settings for Google Analytics
         */
        gaSettings: GA_SETTINGS_PROP_TYPE,
        /*
         * `true` if the user is authenticated
         */
        isAuthenticated: PropTypes.bool,
        /*
         * Current users ID. null if not logged in
         */
        userId: PropTypes.string,
        /*
         * `true` if the user is editing the collection
         */
        isEditMode: PropTypes.bool,
        /*
         * List of organizer IDs for the featured organizers
         */
        organizerIds: PropTypes.arrayOf(PropTypes.string),
        userCollections: COLLECTION_PROP_TYPE,
        onAddOrRemoveEventToCollection: PropTypes.func,
        onCreateCollection: PropTypes.func,
        isQuality: PropTypes.bool,
    };

    render() {
        const {
            events,
            isLoading,
            userHasNoEvents,
            handleOnView,
            handleOnWillShare,
            handleOnDidShare,
            featureFlags,
            emptyStateConfig,
            onSaveEvent,
            userCollections,
            onShowCreateCollectionView,
            onShowAddToCollectionView,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            hasPagination,
            onSeeMore,
            isEditMode,
            isAuthenticated,
            isQuality,
            showFeaturedOrganizers,
        } = this.props;

        if (userHasNoEvents) {
            return (
                <div className="eds-l-mar-top-12" data-spec="no-events">
                    <EmptyState {...emptyStateConfig} />
                </div>
            );
        }

        const eventCardGroupProps = {
            isAuthenticated,
            handleOnView,
            handleOnWillShare,
            handleOnDidShare,
            featureFlags,
            userCollections,
            onShowCreateCollectionView,
            onShowAddToCollectionView,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            handleOnLike: onSaveEvent,
            isEditMode,
            isQuality,
        };

        const mobileComponent = (
            <EventCardGroup
                {...eventCardGroupProps}
                events={_formatEventsForDisplay(events, true)}
                isMobile={true}
            />
        );

        const desktopComponent = (
            <EventCardGroup
                {...eventCardGroupProps}
                events={_formatEventsForDisplay(events, false)}
            />
        );

        const desktopContainerClassNames = classnames('eds-show-up-mn', {
            'events-section__desktop-container': showFeaturedOrganizers,
        });

        return (
            <main data-spec="events-from-following-events">
                <div
                    className={desktopContainerClassNames}
                    data-spec="events-from-following__desktop"
                >
                    {desktopComponent}
                </div>
                <div
                    className="eds-show-down-sw"
                    data-spec="events-from-following__mobile"
                >
                    {mobileComponent}
                </div>
                <SeeMoreButton
                    onClick={onSeeMore}
                    hasPagination={hasPagination}
                    isLoading={isLoading}
                />
            </main>
        );
    }
}
